import { useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { fehlertext } from '../../api/client'
import { InfoIcon, ReisenIcon, TrashIcon } from '../../components/Icons'
import { Netzstreifen } from '../../components/Netzstreifen'
import { rueckfrage } from '../../components/Rueckfrage'
import { usePlaylist, usePlaylistAnlegen, usePlaylistLoeschen, useTrip } from '../../hooks/queries'
import { useOnline } from '../../hooks/useOutbox'
import { Chathintergrund } from '../thread/Chathintergrund'
import { Musikkarte } from '../thread/Musikkarte'
import { Musikwahl } from '../thread/Musikwahl'
import { Reiseumschalter, zeigtEntwurf, zeigtTage } from '../map/Reiseumschalter'
import { ViewBar, MediaSwitch, FloatingHead, headSubtitle, useBarByScroll, useNewNavigation } from '../shell/NewNavigation'
import { useTexte } from '../../texte'

/**
 * Die Playlist einer Reise (#102, 1.58.0) -- die Musik neben Chat und Karte.
 *
 * Aus dem Betrieb: „nur der Autor darf da Musik hinzufügen, löschen etc. wie
 * im Chat. Andere können nur lesen aber nicht antworten. Es ist also im
 * Prinzip ein eingeschränkter Chat nur für Musik und ohne
 * Antwortmöglichkeiten."
 *
 * **„Nur lesen" ist hier die ganze Seite und keine Abfrage je Knopf.** Es
 * gibt keine Eingabezeile, keine Reaktion, kein Zitat und keinen Lesestand --
 * nicht abgeschaltet, sondern nicht gebaut. Was ein Mitlesender nicht sieht,
 * kann auch nicht flackern, und der Server sagt mit `may_edit` dasselbe noch
 * einmal.
 *
 * **Nur online.** Die Seite holt die Player von Spotify und Apple Music, und
 * der Server prüft jeden Link beim Hinzufügen. Ohne Netz steht deshalb ein
 * Satz statt einer Liste -- eine stille Absage wäre die schlechtere Auskunft.
 *
 * **Derselbe Hintergrund wie im Chat**, und zwar aus derselben Stelle
 * (`Chathintergrund`): eine zweite Rechnung liefe auseinander.
 */
export function PlaylistPage() {
  const texte = useTexte()
  const t = texte.playlist
  const { tripId: roh } = useParams()
  const tripId = Number(roh)
  // The new navigation (#174, 1.190.0): the playlist is one of the media.
  const newNav = useNewNavigation()
  const inhalt = useRef<HTMLDivElement | null>(null)
  const [barOpen, setBarOpen, scrolled] = useBarByScroll(inhalt, newNav)
  const online = useOnline()

  const trip = useTrip(tripId)
  const liste = usePlaylist(tripId)
  const anlegen = usePlaylistAnlegen(tripId)
  const loeschen = usePlaylistLoeschen(tripId)
  const [wahlOffen, setWahlOffen] = useState(false)

  /*
   * **Der Server sagt, wer bearbeiten darf** -- und bis seine Antwort da ist,
   * darf niemand. Die Rolle aus der Reise wäre eine zweite Quelle für
   * dieselbe Frage, und genau das flackerte beim Reiter „Bearbeiten" (#26).
   */
  const darfBearbeiten = liste.data?.may_edit === true
  const eintraege = liste.data?.items ?? []

  async function nehmen(id: number): Promise<void> {
    if (await rueckfrage(t.nehmenFrage, t.nehmenKnopf)) {
      loeschen.mutate(id)
    }
  }

  return (
    <>
      {!newNav && (
        <header className="bar">
          <Link className="icon-button" to="/" aria-label={texte.allgemein.zuDenReisen}>
            <ReisenIcon />
          </Link>
          <div className="bar__grow">
            <h2 className="bar__title">{trip.data?.title ?? '…'}</h2>
            <p className="bar__subtitle">
              {eintraege.length === 0 ? t.titel : `${t.titel} · ${t.anzahl(eintraege.length)}`}
            </p>
          </div>
          <Link className="icon-button" to={`/t/${tripId}/info`} aria-label={texte.karte.seite.zurReiseseite}>
            <InfoIcon />
          </Link>
        </header>
      )}

      <Netzstreifen />

      {/* Derselbe Rahmen wie auf der Kartenseite: die schwebende Pille liegt
          absolut darin und nicht am Fenster. */}
      <div className="reiseinhalt reiseinhalt--rollt" data-playlist="">
        {newNav ? (
          <FloatingHead
          title={trip.data?.title}
          subtitle={headSubtitle(trip.data, '')}
          views={{ tripId, active: 'media' }} below={<MediaSwitch tripId={tripId} active="playlist" />}>
            <Link className="glass-group__button" to={`/t/${tripId}/info`} aria-label={texte.karte.seite.zurReiseseite} data-nav-target="info">
              <InfoIcon />
            </Link>
          </FloatingHead>
        ) : (
          <Reiseumschalter
            tripId={tripId}
            tage={zeigtTage(trip.data)}
            aktiv="playlist"
            darfBearbeiten={trip.data?.role === 'sender'}
            entwurf={zeigtEntwurf(trip.data)}
            playlist
          />
        )}
        <Chathintergrund grund={trip.data?.chat_bg} />

        <div className="playlist" ref={inhalt}>
          {!online && (
            <p className="notice" data-playlist-ohne-netz="">
              {t.ohneNetz}
            </p>
          )}

          {online && liste.isPending && <p className="empty">{t.laedt}</p>}

          {liste.isError && (
            <p className="notice" role="alert">
              {fehlertext(liste.error)}
            </p>
          )}

          {online && liste.data !== undefined && eintraege.length === 0 && (
            <p className="empty">{darfBearbeiten ? t.leerAutor : t.leerLeser}</p>
          )}

          {eintraege.map((eintrag) => (
            <article key={eintrag.id} className="playlist__eintrag" data-playlist-eintrag={eintrag.id}>
              {/* The note is the heading, above the card (1.71.0) -- the same
                  class as the text of a music post in the chat. */}
              {eintrag.note !== '' && (
                <h3 className="musikkarte__ueberschrift" data-musik-ueberschrift="">{eintrag.note}</h3>
              )}
              <Musikkarte karte={eintrag} />
              {darfBearbeiten && (
                <button
                  type="button"
                  className="button button--small button--outline playlist__weg"
                  disabled={loeschen.isPending}
                  onClick={() => void nehmen(eintrag.id)}
                  data-playlist-weg={eintrag.id}
                >
                  <TrashIcon size={16} />
                  {t.nehmen}
                </button>
              )}
            </article>
          ))}

          {loeschen.isError && (
            <p className="notice" role="alert">
              {fehlertext(loeschen.error)}
            </p>
          )}

          {/* Der Knopf steht unten und nicht in einer Eingabezeile: es gibt
              hier keine, und eine leere Zeile wäre ein Versprechen auf eine
              Antwort, die die Seite nicht kennt. */}
          {darfBearbeiten && online && (
            <button
              type="button"
              className="button playlist__hinzu"
              onClick={() => setWahlOffen(true)}
              data-playlist-hinzu=""
            >
              {t.hinzufuegen}
            </button>
          )}
        </div>
        {newNav && <ViewBar
          tripId={tripId}
          active="media"
          place="bottom"
          open={barOpen}
          onOpen={setBarOpen}
          scrolled={scrolled}
        />}
      </div>

      {wahlOffen && (
        <Musikwahl
          onSenden={async (eingabe) => {
            // Without the key: a playlist entry is not a post, and its
            // server reads none (1.75.0 covers chat, music, poll, day card).
            await anlegen.mutateAsync({ url: eingabe.url, body: eingabe.body })
          }}
          onZu={() => setWahlOffen(false)}
          wohin="playlist"
          ort={`playlist-${tripId}`}
        />
      )}
    </>
  )
}
